import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Star, BadgeCheck } from 'lucide-react';
import { TESTIMONIALS } from '@/config/testimonials';

/**
 * TestimonialsWall — Phase 93 (Coach Testimonial Collector).
 *
 * Marketing-page testimonial display, shared by Landing + /customers.
 *
 * Owner rules:
 *   - REAL coach reviews (collected in-app, 4-5 stars, consented, not
 *     admin-hidden) render FIRST, marked "Verified coach".
 *   - The placeholder testimonials from /config/testimonials.js stay
 *     BEHIND them until the owner deletes them from that config.
 *   - Once real reviews exist, the row becomes horizontally scrollable
 *     (CSS scroll-snap, same pattern as the coach gallery) and a summary
 *     strip shows avg stars + count + % "helping a lot".
 *   - With no real reviews yet, the original 3-column grid look is kept.
 */

function TCard({ t, real, snap }) {
    const { t: t42 } = useLocaleR42();
    return (
        <Card
            className={`sd-card sd-card-hover p-6 ${snap ? 'w-[320px] shrink-0 snap-start sm:w-[360px]' : ''}`}
            data-testid={real ? 'testimonial-card-real' : 'testimonial-card-placeholder'}
        >
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-1 text-[#ffd700]">
                    {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`h-4 w-4 ${s <= (t.stars || 5) ? 'fill-[#ffd700]' : 'text-[#0a1628]/15'}`} />
                    ))}
                </div>
                {real && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#00d4ff]/15 px-2 py-0.5 text-[10px] font-semibold text-[#0a1628]">
                        <BadgeCheck className="h-3 w-3 text-[#00d4ff]" /> {t42("Verified coach")} </span>
                )}
            </div>
            <p className="text-[#1a3a5c]">{t.q}</p>
            <div className="mt-4 flex items-center gap-3">
                {t.img && <img src={t.img} alt={t.a} className="h-10 w-10 rounded-full object-cover" loading="lazy" />}
                <div>
                    <div className="text-sm font-semibold text-[#0a1628]">{t.a}</div>
                    <div className="text-xs text-[#1a3a5c]/70">{[t.school, t.r].filter(Boolean).join(' · ')}</div>
                    {t.handle && <div className="text-xs text-[#00d4ff]">{t.handle}</div>}
                </div>
            </div>
        </Card>
    );
}

export function TestimonialsWall() {
    const { t: t42 } = useLocaleR42();
    const [real, setReal] = useState([]);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get('/public/testimonials')
            .then((r) => {
                if (!alive) return;
                setReal((r.data?.items || []).map((i) => ({
                    q: `“${i.quote}”`,
                    a: i.name,
                    r: i.location,
                    img: i.photo_url,
                    school: i.school_name,
                    stars: i.stars,
                    real: true,
                })));
                setSummary(r.data?.summary || null);
            })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    const cards = [...real, ...TESTIMONIALS.map((t) => ({ ...t, stars: 5, real: false }))];

    return (
        <div data-testid="testimonials-wall">
            {summary && summary.count >= 1 && (
                <div className="mx-auto mb-8 flex max-w-2xl flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-[#1a3a5c]" data-testid="testimonials-summary">
                    <span className="inline-flex items-center gap-1 font-semibold text-[#0a1628]">
                        <Star className="h-4 w-4 fill-[#ffd700] text-[#ffd700]" />
                        {summary.avg_stars} {t42("average")} </span>
                    <span>{t42("from")} {summary.count} {t42("verified")} {summary.count === 1 ? t42("coach") : t42("coaches")}</span>
                    <span>{summary.pct_helping_a_lot}{t42("% say it’s helping their business a lot")}</span>
                </div>
            )}

            {real.length > 0 ? (
                <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6" data-testid="testimonials-scroll-row">
                    {cards.map((t, i) => <TCard key={`${t.a}-${i}`} t={t} real={t.real} snap />)}
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-3">
                    {cards.map((t, i) => <TCard key={`${t.a}-${i}`} t={t} real={false} />)}
                </div>
            )}
        </div>
    );
}
